import { useState } from "react";
import { ChevronRight, Loader2, RotateCcw, X } from "lucide-react";
import { cn } from "utils/cn";
import { Button } from "components/common/buttons/Button";
import { ProjectCard } from "../ProjectCard";
import { ProjectsGridProps } from "./ProjectsGrid.types";
import { usePendingProjectTasks } from "./usePendingProjectTasks";

const GRID_CLASS = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4";

const pendingStageLabel = (stage: string, uploadProgress?: number): string => {
  switch (stage) {
    case "uploading":
      return typeof uploadProgress === "number"
        ? `Uploading screenplay… ${Math.round(uploadProgress * 100)}%`
        : "Uploading screenplay…";
    case "parsing":
      return "Reading the screenplay…";
    case "finalizing":
      return "Finalizing…";
    default:
      return "Creating project…";
  }
};

/** Placeholder cards for projects still being created in the background. */
const PendingProjectCards = () => {
  const { tasks, backgroundTasks } = usePendingProjectTasks();

  return (
    <>
      {tasks.map((task) => (
        <div
          key={task.id}
          className="flex flex-col overflow-hidden rounded-xl border border-dashed border-border bg-card/60"
        >
          <div className="relative flex aspect-[2/1] items-center justify-center border-b border-dashed border-border bg-muted/30">
            {task.status === "error" ? (
              <span className="h-2.5 w-2.5 rounded-full bg-danger" aria-hidden />
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
            )}
            {task.stage === "uploading" && typeof task.uploadProgress === "number" && (
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-0.5 bg-primary/70 transition-all"
                style={{ width: `${Math.round(task.uploadProgress * 100)}%` }}
              />
            )}
          </div>
          <div className="flex flex-col gap-1 px-4 py-3">
            <span className="truncate text-sm font-semibold text-foreground/80">
              {task.projectName}
            </span>
            <span className="text-xs text-muted-foreground" role="status">
              {task.status === "error"
                ? (task.errorMessage ?? "Creation failed")
                : pendingStageLabel(task.stage, task.uploadProgress)}
            </span>
            {task.status === "error" && backgroundTasks && (
              <span className="mt-1 flex items-center gap-1.5">
                <Button
                  type="button"
                  color="gray"
                  size="xs"
                  className="h-6 gap-1 px-2 text-[11px] [&_svg]:h-3 [&_svg]:w-3"
                  onClick={() => backgroundTasks.retryCreateProjectTask(task.id)}
                >
                  <RotateCcw />
                  Retry
                </Button>
                <Button
                  type="button"
                  color="gray"
                  size="icon"
                  aria-label={`Dismiss failed project ${task.projectName}`}
                  className="h-6 w-6 [&_svg]:h-3.5 [&_svg]:w-3.5"
                  onClick={() => backgroundTasks.dismissCreateProjectTask(task.id)}
                >
                  <X />
                </Button>
              </span>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export const ProjectsGrid = ({
  projects,
  archivedProjects,
  onOpenProject,
  onProjectSettingsClick,
  expandArchived = false,
  leadingTile,
}: ProjectsGridProps) => {
  const [archivedOpen, setArchivedOpen] = useState(false);
  const showArchived = archivedOpen || expandArchived;

  return (
    <div className="flex flex-col gap-6">
      <div className={GRID_CLASS}>
        {leadingTile}
        <PendingProjectCards />
        {projects.map((project, index) => (
          <ProjectCard
            key={project.projectId}
            project={project}
            index={index}
            onOpenProject={onOpenProject}
            onProjectSettingsClick={onProjectSettingsClick}
          />
        ))}
      </div>

      {archivedProjects.length > 0 && (
        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => setArchivedOpen((open) => !open)}
            aria-expanded={showArchived}
            className="flex items-center gap-2 self-start rounded text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <ChevronRight
              className={cn("h-3.5 w-3.5 transition-transform", showArchived && "rotate-90")}
              aria-hidden
            />
            Archived ({archivedProjects.length})
          </button>
          {showArchived && (
            <div className={GRID_CLASS}>
              {archivedProjects.map((project, index) => (
                <ProjectCard
                  key={project.projectId}
                  project={project}
                  index={index}
                  onOpenProject={onOpenProject}
                  onProjectSettingsClick={onProjectSettingsClick}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
